import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../utils/api';
import { Edit2, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { FeedbackList } from '../components/PeerFeedback';

const card = {
  background: 'var(--field)',
  border: '1px solid var(--rule-strong)',
  boxShadow: 'none',
  borderRadius: 16,
  padding: '16px 18px',
  marginBottom: 12,
};

const inputStyle = { padding: '10px 12px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, minHeight: 44 };

// 着座・獲得は total_*（時間別入力からの集計）が入っていればそれ、無ければ着座メモ件数で補完。
const seatOf = (r) => (r.total_seat != null ? r.total_seat : (r.memo_seat || 0));
const acqOf  = (r) => (r.total_acq  != null ? r.total_acq  : (r.memo_acq  || 0));

// 一覧に出す件数カラム（すべて件数）。ファネル順：キャッチ→ティッシュ→トーク→フルトーク→着座→獲得。
const COLS = [
  { key: 'total_catch',     label: 'キャッチ',   color: 'var(--mark)' },
  { key: 'total_sticker',   label: 'ティッシュ回収', color: 'var(--mark)' },
  { key: 'total_talk',      label: 'トーク開始', color: 'var(--mark)' },
  { key: 'total_full_talk', label: 'フルトーク', color: 'var(--mark)' },
  { key: 'seat',            label: '着座',       color: 'var(--mark)', get: seatOf }, // total_seat ?? memo_seat
  { key: 'acq',             label: '獲得',       color: 'var(--gain)', get: acqOf },  // total_acq ?? memo_acq
];

const WD = ['日', '月', '火', '水', '木', '金', '土'];
const dateLabel = (d) => {
  const dt = new Date(d + 'T00:00:00Z');
  return `${dt.getUTCMonth() + 1}月${dt.getUTCDate()}日(${WD[dt.getUTCDay()]})`;
};

function Block({ title, text }) {
  if (!text) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-mute)', marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 44 }}>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color }}>{value ?? 0}</div>
    </div>
  );
}

export default function ReflectionHistory({ user, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // 本人は既定で自分の日報に絞る（ホームのフィードバックカードから来たときに自分の分へ着地）。管理者は全員。
  const [filter, setFilter] = useState({ user_id: user.role === 'admin' ? '' : String(user.id), date_from: '', date_to: '' });
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState({}); // reflection id -> bool

  useEffect(() => {
    api.getUsers().then(setUsers).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter.user_id) params.user_id = filter.user_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    return api.getReflections(params)
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // 日付ごとの括りに再構成（同じ日に全担当者が並ぶ）
  const groups = useMemo(() => {
    const map = {};
    items.forEach(r => { (map[r.reflection_date] = map[r.reflection_date] || []).push(r); });
    return Object.keys(map)
      .sort((a, b) => (a < b ? 1 : -1))
      .map(date => ({
        date,
        rows: map[date].slice().sort((a, b) => acqOf(b) - acqOf(a)),
      }));
  }, [items]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const today = new Date().toLocaleDateString('sv-SE');

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <h1 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800, color: 'var(--ink)'}}> 日報 履歴</h1>

      {/* フィルター（担当者：全員 / 個人） */}
      <div style={{ ...card, padding: '12px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>担当者</label>
          <select value={filter.user_id} onChange={e => setFilter(f => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
            <option value="">全員</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>日付From</label>
          <input type="date" value={filter.date_from} onChange={e => setFilter(f => ({ ...f, date_from: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>日付To</label>
          <input type="date" value={filter.date_to} onChange={e => setFilter(f => ({ ...f, date_to: e.target.value }))} style={inputStyle} />
        </div>
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', paddingBottom: 8 }}>{items.length}件</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-mute)', padding: 40 }}>読み込み中...</div>
      ) : groups.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 60 }}>該当データがありません</div>
      ) : (
        groups.map(g => {
          const dayCost = g.rows.reduce((s, r) => s + (Number(r.booth_cost) || 0), 0);
          return (
          <div key={g.date} style={card}>
            {/* 日付の括りヘッダー */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--rule-strong)' }}>
              <Calendar size={16} color="#33406E" />
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{dateLabel(g.date)}</span>
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{g.rows.length}名</span>
              {dayCost > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 700 }}> ブース代 計 ¥{dayCost.toLocaleString()}</span>
              )}
            </div>

            {/* その日の担当者一覧（タップで詳細） */}
            {g.rows.map(r => {
              const isMine = r.user_id === user.id;
              const open = !!expanded[r.id];
              const hasDetail = r.next_pitch_text || r.why_text || r.improvement_text || r.prevent_stage || r.eval_entry || r.eval_flow || r.eval_close;
              return (
                <div key={r.id} style={{ marginBottom: 8, background: 'var(--paper-sunk)', border: `1px solid ${isMine ? '#33406E' : 'var(--rule-strong)'}`, borderRadius: 12, overflow: 'hidden' }}>
                  {/* 行（担当者名＋件数） */}
                  <div onClick={() => toggle(r.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer' }}>
                    {open ? <ChevronDown size={16} color="var(--ink-mute)" /> : <ChevronRight size={16} color="var(--ink-mute)" />}
                    <div style={{ flex: 1, minWidth: 90 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isMine ? 'var(--mark)' : 'var(--ink)' }}>
                        {r.user_name || `User#${r.user_id}`}
                        {isMine && <span style={{ fontSize: 'var(--t-note)', padding: '1px 6px', background: 'var(--mark-soft)', color: 'var(--mark)', borderRadius: 4, marginLeft: 6 }}>自分</span>}
                        {(r.feedbacks || []).length > 0 && <span title="フィードバックあり"style={{ fontSize: 'var(--t-note)', padding: '1px 6px', background: 'var(--gain-bg)', color: 'var(--gain)', borderRadius: 4, marginLeft: 6 }}> {r.feedbacks.length}件</span>}
                      </div>
                      {r.booth_name && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 2 }}> {r.booth_name}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {COLS.map(c => (
                        <MiniStat key={c.key} label={c.label} value={c.get ? c.get(r) : r[c.key]} color={c.color} />
                      ))}
                    </div>
                  </div>

                  {/* 詳細（展開時） */}
                  {open && (
                    <div style={{ padding: '4px 14px 14px', borderTop: '1px solid var(--rule-strong)' }}>
                      {r.booth_name && (
                        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 8 }}> ブース: <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{r.booth_name}</span></div>
                      )}
                      {r.booth_cost != null && r.booth_cost !== '' && (
                        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 4 }}> ブース代: <span style={{ color: 'var(--hold)', fontWeight: 700 }}>¥{Number(r.booth_cost).toLocaleString()}</span></div>
                      )}
                      {/* 自己評価 */}
                      {(r.eval_entry || r.eval_flow || r.eval_close) ? (
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <MiniStat label="入り" value={r.eval_entry} color="var(--gain)" />
                          <MiniStat label="運び" value={r.eval_flow} color="var(--gain)" />
                          <MiniStat label="決め" value={r.eval_close} color="var(--gain)" />
                          {r.hours_worked != null && <MiniStat label="稼働h" value={r.hours_worked} color="var(--mark)" />}
                        </div>
                      ) : null}
                      <Block title="今日良かったこと"text={r.next_pitch_text} />
                      <Block title="今日の課題"text={r.why_text} />
                      <Block title="明日改善すること"text={r.improvement_text} />
                      <Block title="困っていること"text={r.prevent_stage} />
                      <Block title="明日の改善テーマ（旧）" text={r.tomorrow_theme} />
                      {!hasDetail && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 8 }}>振り返りテキストは未入力です。</div>}
                      <FeedbackList reflection={r} currentUser={user} onChanged={load} />
                      {isMine && r.reflection_date === today && (
                        <button onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate('shift-end'); }} style={{
                          marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '6px 12px', borderRadius: 8,
                          background: 'var(--mark-soft)', border: '1px solid #33406E',
                          color: 'var(--mark)', fontSize: 'var(--t-note)', fontWeight: 600, cursor: 'pointer',
                        }}>
                          <Edit2 size={12} /> 日報を編集
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          );
        })
      )}
    </div>
  );
}
